/**
 * @typedef {Object} PokemonPlanning
 * @property {string} nom
 * @property {string} type
 * @property {string} pcNonBooste - Plage brute, ex. "1166 - 1232".
 * @property {string} pcBooste - Plage brute, ex. "1458 - 1540".
 * @property {string[]} typesAttaquants
 */

/**
 * @typedef {Object} JourPlanning
 * @property {string} jour - Ex. "lundi".
 * @property {string} date - Ex. "24 août".
 * @property {string} evenement - Ex. "Kapoera dynamax" (peut être vide).
 * @property {PokemonPlanning[]} pokemons
 */

/**
 * @typedef {Object} PlanningHebdomadaire
 * @property {string} semaine - Ligne de titre, ex. "Semaine du 24 août 2026".
 * @property {JourPlanning[]} jours
 * @property {string} texteComplementaire - Markdown restant après le planning.
 */

/**
 * @typedef {Object} EtatAnalyse
 * @property {string} semaine
 * @property {JourPlanning[]} jours - Tous les jours rencontrés (avant filtrage).
 * @property {string[]} lignesComplementaires - Lignes de texte libre situées après le planning.
 * @property {JourPlanning | null} jourCourant - Jour auquel se rattachent les titres de niveau 5.
 * @property {PokemonPlanning | null} pokemonCourant - Pokémon auquel se rattachent les puces.
 */

// Titre markdown : 1 à 6 "#" en début de ligne, un espace, puis le texte.
// Groupe 1 = les "#" (leur longueur donne le niveau du titre) ; groupe 2 = le texte.
// Ex. "#### lundi 24 août - Kapoera dynamax" -> ["####", "lundi 24 août - Kapoera dynamax"]
const REGEX_TITRE = /^(#{1,6})\s+(.*)$/

// Puce de liste : un "-" ou "*" en début de ligne suivi d'au moins un espace.
// Groupe 1 = le contenu de la puce.
// Ex. "- Type : Combat" -> ["Type : Combat"]
const REGEX_PUCE = /^[-*]\s+(.*)$/

// En-tête de jour "<jour> <date>" : le premier mot (jour de la semaine) puis le reste (la date).
// Groupe 1 = le jour ; groupe 2 = la date.
// Ex. "lundi 24 août" -> ["lundi", "24 août"]
const REGEX_ENTETE_JOUR = /^(\S+)\s+(.*)$/

// Séparateur "<jour date> - <événement>" dans un titre de jour : un tiret court "-"
// ou un demi-cadratin "–" entouré d'espaces.
// Ex. "lundi 24 août - apéro raid".split(...) -> ["lundi 24 août", "apéro raid"]
const REGEX_SEPARATEUR_EVENEMENT = /\s+[-–]\s+/

// Préfixe à retirer devant la liste des types recommandés.
// Ex. "pokémons de type Fée, Vol et Psy" -> "Fée, Vol et Psy"
const REGEX_PREFIXE_TYPES = /^pok[ée]mons?\s+de\s+type\s+/i

// Séparateurs entre types recommandés : une virgule ou le mot " et ".
// Ex. "Fée, Vol et Psy".split(...) -> ["Fée", "Vol", "Psy"]
const REGEX_SEPARATEUR_TYPES = /\s*,\s*|\s+et\s+/

// Fin de ligne Windows ("\r\n") ou Unix ("\n").
const REGEX_SAUT_DE_LIGNE = /\r?\n/

// Niveaux de titre markdown reconnus dans le gist.
const NIVEAU_TITRE_SEMAINE = 3 // "### Semaine du ..." (1 et 2 sont traités pareil)
const NIVEAU_TITRE_JOUR = 4 // "#### lundi 24 août - ..."
// Niveau >= 5 ("##### Kapoera") : nom d'un pokémon.

/**
 * Sépare une puce "label : valeur" en ses deux parties.
 * Le label est renvoyé en minuscules pour faciliter la comparaison.
 * @param {string} contenu - Contenu d'une puce, ex. "PC boosté : 1458 - 1540".
 * @returns {{label: string, valeur: string}}
 */
function separerPuce(contenu) {
  const index = contenu.indexOf(":")
  if (index === -1) {
    return { label: contenu.trim().toLowerCase(), valeur: "" }
  }
  return {
    label: contenu.slice(0, index).trim().toLowerCase(),
    valeur: contenu.slice(index + 1).trim(),
  }
}

/**
 * Détermine à quelle information correspond le libellé d'une puce.
 * L'ordre des tests gère le cas "Equipe recommandé (type attaque immédiate)"
 * qui contient le mot "type" tout en désignant les attaquants.
 * @param {string} label - Libellé en minuscules (partie avant les ":").
 * @returns {"type" | "pcNonBooste" | "pcBooste" | "attaquants" | null}
 */
function identifierPuce(label) {
  if (label.includes("quipe") || label.includes("recommand") || label.includes("attaqu")) {
    return "attaquants"
  }
  if (label.includes("boost")) {
    return label.includes("non") ? "pcNonBooste" : "pcBooste"
  }
  if (label.includes("type")) {
    return "type"
  }
  return null
}

/**
 * Transforme "pokémons de type Fée, Vol et Psy" en ["Fée", "Vol", "Psy"].
 * @param {string} valeur
 * @returns {string[]}
 */
function extraireTypesAttaquants(valeur) {
  return valeur
    .replace(REGEX_PREFIXE_TYPES, "")
    .split(REGEX_SEPARATEUR_TYPES)
    .map((type) => type.trim())
    .filter(Boolean)
}

/**
 * Renseigne le champ du pokémon correspondant à la puce.
 * @param {PokemonPlanning} pokemon - Pokémon courant, muté sur place.
 * @param {string} contenu - Contenu de la puce, ex. "Type : Combat".
 */
function appliquerPuce(pokemon, contenu) {
  const { label, valeur } = separerPuce(contenu)

  switch (identifierPuce(label)) {
    case "type":
      pokemon.type = valeur
      break
    case "pcNonBooste":
      pokemon.pcNonBooste = valeur
      break
    case "pcBooste":
      pokemon.pcBooste = valeur
      break
    case "attaquants":
      pokemon.typesAttaquants = extraireTypesAttaquants(valeur)
      break
    default:
      break
  }
}

/**
 * Découpe un titre de jour "lundi 24 août - Kapoera dynamax".
 * L'événement peut être absent (pas de tiret) ; le jour et la date sont alors
 * déduits au mieux du texte disponible.
 * @param {string} texte
 * @returns {{jour: string, date: string, evenement: string}}
 */
function analyserEnteteJour(texte) {
  const [avantTiret = "", ...apresTiret] = texte.split(REGEX_SEPARATEUR_EVENEMENT)
  const correspondance = avantTiret.trim().match(REGEX_ENTETE_JOUR)

  return {
    jour: correspondance ? correspondance[1] : avantTiret.trim(),
    date: correspondance ? correspondance[2].trim() : "",
    evenement: apresTiret.join(" - ").trim(),
  }
}

/**
 * Crée un pokémon vide rattaché à un jour.
 * @param {string} nom
 * @returns {PokemonPlanning}
 */
function creerPokemon(nom) {
  return { nom, type: "", pcNonBooste: "", pcBooste: "", typesAttaquants: [] }
}

/**
 * @returns {EtatAnalyse}
 */
function creerEtatInitial() {
  return {
    semaine: "",
    jours: [],
    lignesComplementaires: [],
    jourCourant: null,
    pokemonCourant: null,
  }
}

/**
 * Applique un titre à l'état selon son niveau : semaine (<= 3), jour (4) ou pokémon (>= 5).
 * Un titre de pokémon avant tout jour est ignoré.
 * @param {EtatAnalyse} etat
 * @param {number} niveau - Nombre de "#" du titre.
 * @param {string} texte - Texte du titre, sans les "#".
 */
function traiterTitre(etat, niveau, texte) {
  if (niveau <= NIVEAU_TITRE_SEMAINE) {
    etat.semaine = texte
    return
  }

  if (niveau === NIVEAU_TITRE_JOUR) {
    etat.jourCourant = { ...analyserEnteteJour(texte), pokemons: [] }
    etat.pokemonCourant = null
    etat.jours.push(etat.jourCourant)
    return
  }

  if (etat.jourCourant !== null) {
    etat.pokemonCourant = creerPokemon(texte)
    etat.jourCourant.pokemons.push(etat.pokemonCourant)
  }
}

/**
 * Conserve une ligne vide uniquement entre deux paragraphes de texte libre,
 * afin de préserver la séparation des paragraphes du texte complémentaire.
 * @param {EtatAnalyse} etat
 */
function memoriserLigneVide(etat) {
  if (etat.jourCourant === null && etat.lignesComplementaires.length > 0) {
    etat.lignesComplementaires.push("")
  }
}

/**
 * Mémorise une ligne de texte hors structure (paragraphe situé après le planning).
 * Les lignes rencontrées avant le premier jour sont ignorées (intro éventuelle).
 * @param {EtatAnalyse} etat
 * @param {string} ligne
 */
function memoriserTexteLibre(etat, ligne) {
  if (etat.jourCourant !== null) {
    etat.lignesComplementaires.push(ligne)
  }
}

/**
 * Traite une ligne de markdown en mutant l'état de l'analyse.
 * @param {EtatAnalyse} etat
 * @param {string} ligne
 */
function traiterLigne(etat, ligne) {
  const contenu = ligne.trim()

  if (contenu === "") {
    memoriserLigneVide(etat)
    return
  }

  const titre = contenu.match(REGEX_TITRE)
  if (titre) {
    traiterTitre(etat, titre[1].length, titre[2].trim())
    return
  }

  const puce = contenu.match(REGEX_PUCE)
  if (puce && etat.pokemonCourant !== null) {
    appliquerPuce(etat.pokemonCourant, puce[1])
    return
  }

  memoriserTexteLibre(etat, contenu)
}

/**
 * Construit le planning final à partir de l'état accumulé.
 * Renvoie null si aucun jour ne contient de pokémon.
 * @param {EtatAnalyse} etat
 * @returns {PlanningHebdomadaire | null}
 */
function construireResultat(etat) {
  const joursAvecPokemons = etat.jours.filter((jour) => jour.pokemons.length > 0)
  if (joursAvecPokemons.length === 0) {
    return null
  }

  return {
    semaine: etat.semaine,
    jours: joursAvecPokemons,
    texteComplementaire: etat.lignesComplementaires.join("\n").trim(),
  }
}

/**
 * Analyse le markdown de l'"heure de la semaine" (issu du gist) en un planning structuré.
 * Le format attendu est :
 *   ### Semaine du 24 août 2026
 *   #### lundi 24 août - Kapoera dynamax
 *   ##### Kapoera
 *   - Type : Combat
 *   - PC non boosté : 1166 - 1232
 *   - PC boosté : 1458 - 1540
 *   - Equipe recommandé (...) : pokémons de type Fée, Vol et Psy
 *
 * @param {string} markdown
 * @returns {PlanningHebdomadaire | null} Le planning, ou null si le format est inattendu
 * (aucun jour avec pokémon) afin de laisser l'appelant retomber sur un rendu de secours.
 */
export function parserPlanningHebdomadaire(markdown) {
  if (typeof markdown !== "string" || markdown.trim() === "") {
    return null
  }

  const etat = creerEtatInitial()
  for (const ligne of markdown.split(REGEX_SAUT_DE_LIGNE)) {
    traiterLigne(etat, ligne)
  }

  return construireResultat(etat)
}
